import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TSelectBase } from 'types/general.types';
import type { TVisibility } from 'types/template/template.types';

export const VISIBILITY_VALUES: readonly TVisibility[] = ['global', 'client', 'local'] as const;

/**
 * Display labels for the visibility enum. The stored value stays `global`/`client`/`local`
 * everywhere — backend, shared types package, query filters — and is only humanised at render
 * time, so every surface says the same thing without a data migration.
 */
export const useVisibilityLabels = () => {
  const { t } = useTranslation('common');

  const visibilityLabel = useCallback(
    // Falls back to the raw value so a newly added enum member degrades to something readable
    // rather than a missing-key artifact.
    (visibility?: string | null) => (visibility ? t(`visibility.${visibility}`, { defaultValue: visibility }) : ''),
    [t],
  );

  const visibilityItems = useMemo<TSelectBase[]>(
    () => VISIBILITY_VALUES.map((value) => ({ value, label: visibilityLabel(value) })),
    [visibilityLabel],
  );

  // Noun phrase for the combobox's "Select {{itemName}}..." / "No {{itemName}} found." templates.
  const visibilityItemName = t('visibility.itemName');

  return { visibilityLabel, visibilityItems, visibilityItemName };
};
